import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

// Contexts
import { AuthProvider } from './contexts/AuthContext';

// Hooks
import { useAuthInterceptor } from './hooks/useAuthInterceptor';

// Components
import Sidebar from './components/layout/Sidebar';
import Header from './components/layout/Header';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';

// Pages
import Dashboard from './pages/Dashboard';
import Bookings from './pages/Bookings';
import Orders from './pages/Orders';
import Clients from './pages/Clients';
import Coaches from './pages/Coaches';
import Services from './pages/Services';
import ServiceClients from './pages/ServiceClients';
import Schedule from './pages/Schedule';
import Notifications from './pages/Notifications';
import Analytics from './pages/Analytics';
import Settings from './pages/Settings';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import NotFound from './pages/NotFound';
import ServerError from './pages/ServerError';
import Unauthorized from './pages/Unauthorized';

// Component that uses the auth interceptor
function AppContent() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Set up auth interceptor
  useAuthInterceptor();

  return (
      <Router>
        <Routes>
          {/* Error pages - accessible without authentication */}
          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route path="/server-error" element={<ServerError />} />

          {/* Protected admin routes */}
          <Route path="/*" element={
            <ProtectedRoute>
              <div className="min-h-screen bg-gray-50 flex">
                <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />

                <div className="flex-1 flex flex-col lg:ml-0">
                  <Header onMenuClick={() => setSidebarOpen(true)} />

                  <main className="flex-1 p-6">
                    <Routes>
                      <Route path="/" element={<Dashboard />} />
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/bookings" element={<Bookings />} />
                      <Route path="/orders" element={<Orders />} />
                      <Route path="/clients" element={<Clients />} />
                      <Route path="/coaches" element={<Coaches />} />
                      <Route path="/services" element={<Services />} />
                      <Route path="/services/:serviceId/clients" element={<ServiceClients />} />
                      <Route path="/schedule" element={<Schedule />} />
                      <Route path="/notifications" element={<Notifications />} />
                      <Route path="/analytics" element={<Analytics />} />
                      <Route path="/settings" element={<Settings />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </main>
                </div>
              </div>
            </ProtectedRoute>
          } />
        </Routes>

        {/* Toast notifications */}
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              duration: 3000,
              theme: {
                primary: '#10b981',
                secondary: '#fff',
              },
            },
          }}
        />
      </Router>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
